import { createSelector } from 'reselect';
import { every, some as someCapped } from 'lodash';
import {
  map,
  some,
  set,
  get,
  pick,
  filter,
  size,
  flow,
  isEmpty,
  isNull,
  isDate,
  find,
} from 'lodash/fp';
import * as assignHelpers from 'actions/assign/helpers';

const [CONTENT, ASSIGNEES, SCHEDULING, REVIEW, SUBMIT] =
  'CONTENT ASSIGNEES SCHEDULING REVIEW SUBMIT'.split(' ');
const STEPS = [CONTENT, ASSIGNEES, SCHEDULING, REVIEW];

const selectionSelector = state => state.selection;
const dataSelector = state => state.data;
const journeyDataSelector = state => state.journeyData;
const scheduleSelector = state => state.schedule;
const stepSelector = state => state.activeStep;
const featuresSelector = state => state.meta.features;

export const getSelectedContentCount = createSelector(
  [selectionSelector],
  selection => selection.content.data.length,
);

export const getActiveStepSelection = createSelector(
  [selectionSelector, stepSelector],
  ({ content, assignees }, step) =>
    get(step, {
      CONTENT: content.data,
      ASSIGNEES: assignees.data,
    }),
);

export const getAssignableData = createSelector(
  [dataSelector, getActiveStepSelection, stepSelector],
  (data, selection) => {
    const getIdentity = pick(['id', 'type']);
    const setAsSelected = set('selected', true);
    const setSelected = item => setAsSelected(item);
    const hasSelectedItem = item => some(getIdentity(item))(selection);
    const updateSelected = item => {
      if (hasSelectedItem(item)) {
        return setSelected(item);
      }
      return item;
    };

    return map(updateSelected)(data);
  },
);

export const getAssignableJourneyData = createSelector(
  [journeyDataSelector, getActiveStepSelection, stepSelector],
  (journeyData, selection) => {
    const getIdentity = pick(['id', 'type']);
    const setAsSelected = set('selected', true);
    const setSelected = item => setAsSelected(item);
    const hasSelectedItem = item => some(getIdentity(item))(selection);
    const updateSelected = item => {
      if (hasSelectedItem(item)) {
        return setSelected(item);
      }
      return item;
    };

    return map(updateSelected)(journeyData);
  },
);

export const getSelectedUsersCount = createSelector(
  [selectionSelector],
  selection => flow(filter({ type: 'User' }), size)(selection.assignees.data),
);

export const getSelectedGroupsCount = createSelector(
  [selectionSelector],
  selection => flow(filter({ type: 'Group' }), size)(selection.assignees.data),
);

export const hasSelectedMixedAssigneeTypes = createSelector(
  [getSelectedUsersCount, getSelectedGroupsCount],
  (usersCount, groupsCount) => !!usersCount && !!groupsCount,
);

/**
 * Requirments
 */

export const assertContentSelection = createSelector(
  selectionSelector,
  selection => !isEmpty(selection.content.data),
);

export const assertAssigneesSelection = createSelector(
  selectionSelector,
  selection => !isEmpty(selection.assignees.data),
);

export const assertAssignDate = createSelector(
  [featuresSelector, scheduleSelector],
  (features, { assignDate }) =>
    isNull(assignDate) || assignDate instanceof Date,
);

export const assertDueDate = createSelector(
  [scheduleSelector],
  ({ dueDate }) => isNull(dueDate) || dueDate instanceof Date,
);

export const assertRecurring = createSelector(
  scheduleSelector,
  ({ recurring, recurringOptions }) => {
    if (!recurring) return true;

    const assignOnValid = every([
      recurringOptions.assignOnAmount,
      recurringOptions.assignOnUnit,
    ]);

    /*
     * We don't require a due date for recurring assignments, so if you have no due date fields selected, this is valid.
     * However if you have selected an amount, but not a unit, this is invalid, so it is either, all or nothing.
     */
    const dueDateValid =
      every([recurringOptions.dueDateAmount, recurringOptions.dueDateUnit]) ||
      every([!recurringOptions.dueDateAmount, !recurringOptions.dueDateUnit]);

    return assignOnValid && dueDateValid;
  },
);

export const assertScheduling = createSelector(
  [assertAssignDate, assertDueDate, assertRecurring],
  (...args) => every(args),
);

export const isReadyToAssign = createSelector(
  [assertContentSelection, assertAssigneesSelection, assertScheduling],
  (...conditions) => every(conditions),
);

export const isRecurringAssignment = createSelector(
  [scheduleSelector, assertRecurring],
  ({ recurring }, recurringAsserted) => recurring && recurringAsserted,
);

export const hasScheduleOptions = createSelector(
  [scheduleSelector, assertAssignDate, isRecurringAssignment],
  ({ dueDate }, hasAssignDate, isRecurring) =>
    someCapped([isRecurring, hasAssignDate, isDate(dueDate)]),
);

export const isReviewing = createSelector(
  stepSelector,
  activeStep => activeStep === REVIEW,
);

export const getStepsAssertion = createSelector(
  [assertContentSelection, assertAssigneesSelection, assertScheduling],
  (...assertions) =>
    [CONTENT, ASSIGNEES, SCHEDULING].map((step, i) => ({
      name: step,
      completed: assertions[i],
    })),
);

export const getNextRequiredStep = createSelector(
  [stepSelector, getStepsAssertion],
  (activeStep, stepsAssertion) =>
    find(step => step.name !== activeStep && !step.completed, stepsAssertion),
);

export const getNextStep = createSelector(
  [stepSelector, getNextRequiredStep, isReviewing],
  (activeStep, nextRequiredStep, isAtReview) => {
    if (nextRequiredStep) return nextRequiredStep.name;
    return isAtReview ? SUBMIT : REVIEW;
  },
);

export const getPreviousStep = createSelector(
  stepSelector,
  activeStep => STEPS[STEPS.indexOf(activeStep) - 1],
);

export const isActiveStepCompleted = createSelector(
  [stepSelector, getStepsAssertion],
  (activeStep, stepsAssertion) => {
    const step = find({ name: activeStep }, stepsAssertion);
    const completed = get('completed', step);
    return completed != null ? completed : true;
  },
);

export const buildAssignment = createSelector(
  s => s,
  state => assignHelpers.buildAssignment(null, state),
);

export const buildJourneyAssignment = createSelector(
  s => s,
  state => assignHelpers.buildAssignment(null, state, 'Journeys'),
);
